import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import './ForgotPassword.css'; // Reuse auth page styles

function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const token = searchParams.get('token');
  const teamId = searchParams.get('teamId');

  // Form state for new users
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    const fetchInviteDetails = async () => {
      if (!token || !teamId) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.getInviteDetails(token, teamId);
        setInviteDetails(response.data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load invitation');
      } finally {
        setLoading(false);
      }
    };

    fetchInviteDetails();
  }, [token, teamId]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAccept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validation for new users
    if (!inviteDetails.isExistingUser) {
      if (!formData.firstName || !formData.lastName || !formData.password) {
        setError('All fields are required');
        setSubmitting(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setSubmitting(false);
        return;
      }

      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        setSubmitting(false);
        return;
      }
    }

    try {
      await authAPI.acceptInvite({
        token,
        teamId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
      });

      // Success - show toast then redirect
      setMessage({
        type: 'success',
        text: 'Invitation accepted! Redirecting to login...',
      });
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>⏳ Loading Invitation...</h1>
            <p>Please wait</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !inviteDetails) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Invalid Invitation</h1>
            <p>{error}</p>
          </div>
          <div className="error-message">
            This invitation link is invalid or has expired.
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/login')}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>You've Been Invited!</h1>
          <p>
            <strong>{inviteDetails.orgAdminName}</strong> invited you to join
          </p>
          {inviteDetails.team && (
            <div
              style={{
                background: 'white',
                border: '2px solid #48bb78',
                padding: '16px',
                borderRadius: '6px',
                marginTop: '16px',
              }}
            >
              <h2 style={{ margin: '0 0 8px 0', color: '#48bb78' }}>
                {inviteDetails.team.sport === 'Softball' ? '🥎' : '⚾' } {inviteDetails.team.name}
              </h2>
              <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
                {inviteDetails.team.sport}
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleAccept}>
          {!inviteDetails.isExistingUser ? (
            <>
              <p style={{ marginBottom: '20px', color: '#718096' }}>
                Create your account to get started:
              </p>

              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={inviteDetails.email}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  required
                />
              </div>
            </>
          ) : (
            <p style={{ marginBottom: '20px', color: '#718096' }}>
              Logged in as <strong>{inviteDetails.email}</strong>. Click accept
              to join the team.
            </p>
          )}

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Accepting...' : 'Accept Invitation'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/login')}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </div>
      {/* Toast Notifications */}
      {message.text && message.type === 'success' && (
        <div className="toast-message toast-success">{message.text}</div>
      )}
      {message.text && message.type === 'error' && (
        <div className="toast-message toast-error">{message.text}</div>
      )}
    </div>
  );
}

export default AcceptInvite;
